import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";

/**
 * @typedef {object} AdminCouriersPagination
 * @property {number} page
 * @property {number} rowsPerPage
 */

/**
 * @typedef {object} AdminCouriersFilter
 * @property {string} search
 */

/**
 * @typedef {object} AdminCouriersSorting
 * @property {string} orderBy
 * @property {string} order
 */

/**
 * @typedef {object} AdminCourierData
 * @property {number} id
 * @property {string} name
 * @property {object} configuration
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * Initializes state from localStorage.
 * @returns {{filters: AdminCouriersFilter, pagination: AdminCouriersPagination, sorting: AdminCouriersSorting}}
 */
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("adminCouriersFilter");
  const defaults = {
    filters: {
      search: "",
    },
    pagination: {
      page: 1,
      rowsPerPage: 20,
    },
    sorting: {
      orderBy: "name",
      order: "asc",
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    try {
      const finalState = {
        filters: { ...defaults.filters, ...(savedState.filters || {}) },
        pagination: {
          ...defaults.pagination,
          ...(savedState.pagination || {}),
        },
        sorting: { ...defaults.sorting, ...(savedState.sorting || {}) },
      };
      return finalState;
    } catch (error) {
      console.error(
        `Error obteniendo los datos del localStorage "adminCouriersFilter": ${error}`,
      );
      return defaults;
    }
  }
  return defaults;
};

/**
 * Custom Hook to manage DATA state for the Admin Setting Couriers page
 *
 * Handles data fetching (GET), templates fetching, pagination, sorting, filtering,
 * and CRUD operations (POST, PATCH, DELETE)
 * It does NOT manage UI state
 *
 * @returns {{
 * adminCouriers: Array<AdminCourierData>,
 * templates: object,
 * loading: boolean,
 * error: string | null,
 * pagination: object,
 * filters: object,
 * sorting: object,
 * adminCouriersCount: number,
 * handleChangePage: (newPage: number) => void,
 * handleChangeRowsPerPage: (newRows: number) => void,
 * handleSortChange: (orderBy: string) => void,
 * createCourier: (courierData: object) => Promise<void>,
 * updateCourier: (courierId: number, courierData: object) => Promise<void>,
 * deleteCourier: (courierId: number) => Promise<void>,
 * refetchAdminCouriers: () => Promise<void>
 * }}
 */
export function useAdminCouriers() {
  // Data status and IU
  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [sorting, setSorting] = useState(initialState.sorting);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [adminCouriers, setAdminCouriers] = useState([]);
  const [templates, setTemplates] = useState({});
  const [adminCouriersCount, setAdminCouriersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const handleSortChange = useCallback((orderBy) => {
    setSorting((previous) => ({
      orderBy: orderBy,
      order:
        previous.orderBy === orderBy && previous.order === "asc"
          ? "desc"
          : "asc",
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const fetchAdminCouriers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      page: pagination.page,
      page_size: pagination.rowsPerPage,
    };

    try {
      const [couriersRes, templatesRes] = await Promise.all([
        axios.get("admins/get_admin_setting_couriers/", { params }),
        axios.get("admins/get_courier_templates/"),
      ]);

      const couriersData = Array.isArray(couriersRes.data)
        ? couriersRes.data
        : couriersRes.data.results;
      const count = Array.isArray(couriersRes.data)
        ? couriersRes.data.length
        : couriersRes.data.count;

      setAdminCouriers(couriersData);
      setAdminCouriersCount(count);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error(
        `No se pudo obtener las configuraciones de courier: ${error}`,
      );
      setError("No se pudo obtener las configuraciones de courier");
    } finally {
      setLoading(false);
    }
  }, [pagination, filters, sorting]);

  const createCourier = useCallback(
    async (courierData) => {
      setLoading(true);
      setError(null);
      try {
        await axios.post("admins/create_admin_setting_courier/", {
          setting_courier: courierData,
        });
        fetchAdminCouriers();
      } catch (error) {
        console.error(
          `Error al crear una nueva credencial de courier: ${error}`,
        );
        setError("Error al crear una nueva credencial de courier");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchAdminCouriers],
  );

  const updateCourier = useCallback(
    async (settingCourierId, courierData) => {
      setLoading(true);
      setError(null);
      try {
        await axios.patch(
          `admins/update_admin_setting_courier/${settingCourierId}/`,
          {
            setting_courier: courierData,
          },
        );
        fetchAdminCouriers();
      } catch (error) {
        console.error(`Error al actualizar la credencial de courier: ${error}`);
        setError("Error al actualizar la credencial de courier");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchAdminCouriers],
  );

  const deleteCourier = useCallback(
    async (settingCourierId) => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(
          `admins/delete_admin_setting_courier/${settingCourierId}/`,
        );
        fetchAdminCouriers();
      } catch (error) {
        console.error(
          `Error al intentar eliminar la credencial de courier: ${error}`,
        );
        setError("Error al intentar eliminar la credencial de courier");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchAdminCouriers],
  );

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    localStorage.setItem(
      "adminCouriersFilter",
      JSON.stringify({
        filters,
        pagination,
        sorting,
      }),
    );
  }, [filters, pagination, sorting]);

  useEffect(() => {
    fetchAdminCouriers();
  }, [fetchAdminCouriers]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    adminCouriers,
    templates,
    loading,
    error,
    pagination,
    filters,
    sorting,
    adminCouriersCount,

    // MANNERS (Setters y Actions)
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
    createCourier,
    updateCourier,
    deleteCourier,
    refetchAdminCouriers: fetchAdminCouriers,
  };
}
